import { ref } from 'vue'
import { useRuntimeConfig, useNuxtApp, useRouter, useI18n  } from '#imports'
import { useCart, useSessionContext } from '@shopware/composables'
import type { Schemas } from '#shopware'

interface Debounced<T extends (...args: unknown[]) => void> {
    (...args: Parameters<T>): void
    // Cancels the pending timer and runs the last scheduled call immediately, if any.
    flush: () => void
}

function debounce<T extends (...args: unknown[]) => void>(func: T, wait: number): Debounced<T> {
    let timeout: ReturnType<typeof setTimeout> | undefined
    let lastArgs: Parameters<T> | undefined

    const run = () => {
        if (timeout == null) return
        clearTimeout(timeout)
        timeout = undefined
        const args = lastArgs
        lastArgs = undefined
        if (args) func(...args)
    }

    const debounced = ((...args: Parameters<T>) => {
        lastArgs = args
        clearTimeout(timeout)
        timeout = setTimeout(run, wait)
    }) as Debounced<T>

    debounced.flush = run

    return debounced
}

function mapCartItems(lineItems: Schemas['LineItem'][]): { item: string, price: number, quantity: number }[] {
    return lineItems
        .filter(item => item.type === 'product' && item.payload?.productNumber != null)
        .map(item => ({
            item: item.payload!.productNumber!,
            price: item.price?.unitPrice ?? 0,
            quantity: item.quantity ?? 1
        }))
}

function mapOrderPurchase(order: Schemas['Order']): { orderId: string | undefined, items: { item: string, price: number, quantity: number }[] } {
    return {
        orderId: order.orderNumber,
        items: (order.lineItems ?? [])
            .filter(item => item.type === 'product' && item.payload?.productNumber != null)
            .map(item => ({
                item: item.payload!.productNumber!,
                price: item.unitPrice ?? 0,
                quantity: item.quantity
            }))
    }
}

function mapModelToEmarsysLogic(model: string): string {
    const logicMap: Record<string, string> = {
        'related': 'RELATED',
        'personal': 'PERSONAL'
    }
    return logicMap[model] ?? model.toUpperCase()
}

export function useEmarsys() {
    const runtimeConfig = useRuntimeConfig().public

    if (!runtimeConfig.emarsysMerchantId) return

    const nuxtApp = useNuxtApp()
    const router = useRouter()
    const { cart } = useCart()
    const { sessionContext } = useSessionContext()
    const { locale } = useI18n()

    const lastSearchTerm = ref('')
    let cartAddedToCurrentBatch = false

    const debouncedEmarsysGo = debounce(() => {
        if (window.ScarabQueue) {
            window.ScarabQueue.push(['go'])
            cartAddedToCurrentBatch = false
        }
    }, 600)

    const getCurrencyCode = () =>
        sessionContext.value?.currency?.isoCode?.toLowerCase() ?? 'eur'

    const getLanguageCode = () =>
        locale.value?.toLowerCase() ?? 'en'

    const getExternalId = () =>
        (sessionContext.value as { extensions?: { emarsys?: { externalId?: string } } })?.extensions?.emarsys?.externalId

    function addCartToQueue(): boolean {
        if (!window.ScarabQueue || cartAddedToCurrentBatch) return false
        const cartItems = mapCartItems(cart.value?.lineItems ?? [])
        window.ScarabQueue.push(['cart', cartItems])
        cartAddedToCurrentBatch = true
        return true
    }

    function trackEmarsysEvent(command?: string, data?: unknown) {
        if (!window.ScarabQueue) return

        try {
            if (runtimeConfig.emarsysTestMode) {
                window.ScarabQueue.push(['testMode'])
            }

            const externalId = getExternalId()
            if (externalId) {
                window.ScarabQueue.push(['setCustomerId', externalId])
            }

            window.ScarabQueue.push(['displayCurrency', getCurrencyCode()])
            window.ScarabQueue.push(['language', getLanguageCode()])

            addCartToQueue()

            if (command) {
                window.ScarabQueue.push(data != null ? [command, data] : [command])
            }

            debouncedEmarsysGo()
        } catch (error) {
            console.warn('[Emarsys] Tracking error:', error)
        }
    }

    // Cart hooks
    nuxtApp.hook('addToCart', () => {
        cartAddedToCurrentBatch = false
        trackEmarsysEvent()
    })

    nuxtApp.hook('removeFromCart', () => {
        cartAddedToCurrentBatch = false
        trackEmarsysEvent()
    })

    nuxtApp.hook('updateCart', () => {
        cartAddedToCurrentBatch = false
        trackEmarsysEvent()
    })

    nuxtApp.hook('viewCart', () => {
        trackEmarsysEvent()
    })

    // Product hooks
    nuxtApp.hook('viewProductDetail', ({ product }) => {
        if (!product?.productNumber) return
        trackEmarsysEvent('view', product.productNumber)
    })

    // Category hooks
    nuxtApp.hook('viewProductListing', ({ categoryPath }) => {
        if (!categoryPath) return
        trackEmarsysEvent('category', categoryPath)
    })

    // Purchase hooks
    nuxtApp.hook('placeOrder', ({ order }) => {
        if (!order) return
        const purchaseData = mapOrderPurchase(order)
        trackEmarsysEvent('purchase', purchaseData)
    })

    // Search hook — searchApiSearchView (A4G Search API) just needs the search term
    // off the route.
    function trackSearchTermFromRoute(): void {
        const currentRoute = router.currentRoute.value
        let searchQuery = ''
        for (const [key, value] of Object.entries(currentRoute.query)) {
            if (key.endsWith('[query]') && typeof value === 'string') {
                searchQuery = value
                break
            }
        }

        if (searchQuery && searchQuery !== lastSearchTerm.value) {
            lastSearchTerm.value = searchQuery
            trackEmarsysEvent('searchTerm', searchQuery)
        }
    }

    nuxtApp.hook('searchApiSearchView', trackSearchTermFromRoute)

    // Widget rendering hook
    nuxtApp.hook('renderEmarsysWidget', ({ containerId, model, limit, successCallback, errorCallback }) => {
        if (!containerId || !model) {
            console.warn('[Emarsys] containerId and model are required for widget rendering')
            return
        }

        if (!window.ScarabQueue) return

        try {
            if (runtimeConfig.emarsysTestMode) {
                window.ScarabQueue.push(['testMode'])
            }

            const externalId = getExternalId()
            if (externalId) {
                window.ScarabQueue.push(['setCustomerId', externalId])
            }

            window.ScarabQueue.push(['displayCurrency', getCurrencyCode()])
            window.ScarabQueue.push(['language', getLanguageCode()])
            addCartToQueue()
            window.ScarabQueue.push(['availabilityZone', 'EMEA'])

            const emarsysLogic = mapModelToEmarsysLogic(model)
            const recommendParams: Record<string, unknown> = {
                logic: emarsysLogic,
                containerId,
                limit: limit ?? 5
            }

            if (successCallback) {
                recommendParams.success = (SC: Parameters<NonNullable<typeof successCallback>>[0]) => {
                    try {
                        successCallback(SC)
                    } catch (error) {
                        console.warn('[Emarsys] Success callback error:', error)
                        errorCallback?.(error)
                    }
                }
            } else {
                recommendParams.templateId = `${containerId}-template`
            }

            window.ScarabQueue.push(['recommend', recommendParams])
            debouncedEmarsysGo()
        } catch (error) {
            console.warn('[Emarsys] Widget rendering error:', error)
            errorCallback?.(error)
        }
    })

    // Flush any pending 'go' for the page being left before the URL changes, so its
    // commands (e.g. a product 'view') are sent while ScarabQueue's own page-context
    // still matches that page, instead of being batched with the next page's commands
    // and picking up its URL (Scarab's OrderedItemEventQueue rejects >1 'view' per 'go').
    router.beforeEach((to, from) => {
        if (to.path === from.path) return
        debouncedEmarsysGo.flush()
    })

    // SPA navigation tracking
    router.afterEach((to, from) => {
        if (to.path === from.path) return

        const fromIsSearch = from.path.includes('/search')
        const toIsSearch = to.path.includes('/search')
        if (fromIsSearch && !toIsSearch) {
            lastSearchTerm.value = ''
        }

        trackEmarsysEvent()
    })

    return { trackEmarsysEvent }
}